import {
  Box,
  Paper,
  Typography,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton,
} from '@mui/material';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

import { ChatMessage as ChatMessageType } from '@/types/chatWidget';

import { useGetNavbarFlags } from '@/api/endpoints/propertyManager/useGetNavbarFlags';
import {
  useStreamCommunicate,
  StreamMessage,
} from '@/api/endpoints/simulation/useStreamCommunicate';
import { Send01Icon } from '@/assets/icons/Send01Icon';
import { ChatWidgetProvider, useChatWidget } from '@/context/ChatWidgetContext';
import { usePropertyManager } from '@/context/PropertyManagerProvider';

import { ChatMessage } from '@/components/chat-widget';

// Type for messages with type property
type ChatMessageWithType = ChatMessageType & { type?: 'general' | 'documents' };
type ChatType = 'general' | 'documents';

const ChatInterface: React.FC = () => {
  const theme = useTheme();
  const { state, sendMessage, setSessionId } = useChatWidget();
  const { selectedPropertyLocation } = usePropertyManager();

  // Get navbar flags to check if communication items should be shown
  const navbarFlagsQuery = useGetNavbarFlags(selectedPropertyLocation?.id);
  const showCommunicationMenuItems = navbarFlagsQuery.data?.showCommunicationMenuItems ?? true;

  const [message, setMessage] = useState('');
  // Always use 'documents' when communication menu items are hidden
  const [chatType, setChatType] = useState<ChatType>('documents');
  const [isTyping, setIsTyping] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreamingActive, setIsStreamingActive] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Use refs to avoid stale closure issues
  const streamingResponseRef = useRef('');
  const isStreamingActiveRef = useRef(false);

  // Force re-render function
  const [, forceUpdate] = useState({});
  const triggerRerender = () => forceUpdate({});

  // Enhanced setters that update both state and ref
  const setStreamingResponseSafe = useCallback((value: string) => {
    streamingResponseRef.current = value;
    setStreamingResponse(value);
    triggerRerender();
  }, []);

  const setIsStreamingActiveSafe = useCallback((value: boolean) => {
    isStreamingActiveRef.current = value;
    setIsStreamingActive(value);
    triggerRerender();
  }, []);

  // Handle streaming message responses
  const handleStreamMessage = useCallback(
    async (streamMessage: StreamMessage) => {
      switch (streamMessage.type) {
      case 'response':
      case 'status':
      case 'document':
        if (streamMessage.response?.email) {
          // Turn off typing indicator and show streaming content
          setIsTyping(false);
          const emailContent = streamMessage.response.email;
          setIsStreamingActiveSafe(true);
          setStreamingResponseSafe(emailContent);

          // Also handle session ID from response
          if (streamMessage.response?.session_id) {
            setSessionId(streamMessage.response.session_id);
          }
        } else if (streamMessage.message && streamMessage.message !== 'AI response received') {
          setIsTyping(false);
          setStreamingResponseSafe(streamMessage.message);
          setIsStreamingActiveSafe(true);
        }
        break;
      case 'completed':
        if (streamingResponseRef.current.length > 0) {
          await sendMessage(streamingResponseRef.current, 'system', 'general');
        }
        setStreamingResponseSafe('');
        setIsStreamingActiveSafe(false);
        setIsTyping(false);

        break;
      case 'error':
        // Handle error case
        if (streamMessage.error) {
          await sendMessage(
            'Sorry, there was an error processing your request.',
            'system',
            'general'
          );
        }
        setStreamingResponseSafe('');
        setIsStreamingActiveSafe(false);
        setIsTyping(false);
        break;
      default:
        // Handle other message types like 'connected', 'status', 'document'
        break;
      }

      // Handle session ID if provided in any message type
      if (streamMessage.message && streamMessage.message.includes('session_id:')) {
        const sessionIdMatch = streamMessage.message.match(/session_id:([^,\s]+)/);
        if (sessionIdMatch) {
          setSessionId(sessionIdMatch[1]);
        }
      }
    },
    [sendMessage, setSessionId, setStreamingResponseSafe, setIsStreamingActiveSafe]
  );

  // Handle streaming completion
  const handleStreamComplete = useCallback(() => {
    setIsTyping(false);
    setStreamingResponse('');
    setIsStreamingActive(false);
  }, []);

  // Handle streaming errors
  const handleStreamError = useCallback((error: Error) => {
    setIsTyping(false);
    setStreamingResponse('');
    setIsStreamingActive(false);
  }, []);

  const { startStream, isStreaming } = useStreamCommunicate({
    onMessage: handleStreamMessage,
    onComplete: handleStreamComplete,
    onError: handleStreamError,
  });

  // Update chat type to 'documents' when communication menu items are hidden
  useEffect(() => {
    if (!showCommunicationMenuItems) {
      setChatType('documents');
    }
  }, [showCommunicationMenuItems]);

  // Suggested questions for empty state
  const suggestedQuestions = [
    'Can you summarize the HOA bylaws?',
    'What does my latest water bill say?',
    'What are the rules about exterior modifications?',
    'What are the late payment penalties?',
    'What are the community quiet hours?',
    'Can you find information about parking permits?',
  ];

  // Memoized filtered messages for general chat
  const generalMessages = useMemo(
    () =>
      (state.messages as ChatMessageWithType[]).filter(
        msg => (msg.type ?? 'general') === 'general'
      ),
    [state.messages]
  );

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [generalMessages, isTyping, streamingResponse]);

  const handleSendMessage = async () => {
    if (message.trim() && selectedPropertyLocation) {
      setIsTyping(true);
      setStreamingResponse('');
      setIsStreamingActive(false);

      try {
        // First update UI with user message
        await sendMessage(message.trim(), 'user', 'general');

        // Start streaming request
        const streamRequest = {
          message: message.trim(),
          ...(state.session_id && { session_id: state.session_id }),
          ...(selectedPropertyLocation?.id && {
            property_id: selectedPropertyLocation.id.toString(),
            location_id: selectedPropertyLocation.id.toString(),
          }),
          chat_type: chatType,
        };

        startStream(streamRequest);

        // Note: Session ID will be handled in the stream response
        // If no session_id exists, the backend will create one and send it back
      } catch (error) {
        setIsTyping(false);
        setIsStreamingActive(false);
      } finally {
        setMessage('');
      }
    }
  };

  const handleSuggestedQuestion = (question: string) => {
    setMessage(question);
  };

  const isMutationLoading = isStreaming;

  return (
    <Box sx={{ height: '87vh', display: 'flex', flexDirection: 'column', bgcolor: '#fafafa' }}>
      {/* Chat Section */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          mx: 'auto',
          width: '100%',
          minHeight: 0, // Important for flex child with overflow
        }}
      >
        {/* Messages Area - Scrollable */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            minHeight: 0, // Important for flex child with overflow
          }}
        >
          {generalMessages.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: '200px',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  textAlign: 'center',
                  color: theme.palette.text.secondary,
                  mb: 2,
                  fontWeight: 500,
                  fontSize: '1.1rem',
                }}
              >
                Start by asking a question about your documents
              </Typography>

              {/* Suggested Questions */}
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  justifyContent: 'center',
                  maxWidth: '600px',
                  mx: 'auto',
                }}
              >
                {suggestedQuestions.map(question => (
                  <Chip
                    key={question}
                    label={question}
                    onClick={() => handleSuggestedQuestion(question)}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: theme.palette.primary.main,
                        borderColor: theme.palette.primary.main,
                      },
                      borderRadius: 3,
                      fontSize: '0.8rem',
                      borderColor: theme.palette.divider,
                      '& .MuiChip-label': {
                        lineHeight: 1.2,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            generalMessages.map(msg => <ChatMessage key={msg.id} message={msg} />)
          )}

          {/* Show streaming response in real-time */}
          {isStreamingActive && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: '#f5f5f5',
                  borderRadius: 3,
                  maxWidth: '75%',
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'flex-start',
                }}
              >
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '14px',
                    fontWeight: 400,
                    lineHeight: 1.285,
                    whiteSpace: 'pre-wrap',
                    color: theme.palette.text.primary,
                    flex: 1,
                  }}
                >
                  {streamingResponse || ' '}
                </Typography>
                {/* Blinking cursor to indicate streaming */}
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    width: '2px',
                    height: '16px',
                    backgroundColor: theme.palette.primary.main,
                    ml: 1,
                    verticalAlign: 'text-bottom',
                    flexShrink: 0,
                    animation: 'blink 1.2s infinite',
                    '@keyframes blink': {
                      '0%, 60%': { opacity: 1 },
                      '61%, 100%': { opacity: 0 },
                    },
                  }}
                />
                {/* Small streaming indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: theme.palette.primary.main,
                      animation: 'pulse 1.5s infinite',
                      '@keyframes pulse': {
                        '0%': { opacity: 0.3, transform: 'scale(1)' },
                        '50%': { opacity: 1, transform: 'scale(1.2)' },
                        '100%': { opacity: 0.3, transform: 'scale(1)' },
                      },
                    }}
                  />
                </Box>
              </Paper>
            </Box>
          )}

          {(isTyping || isMutationLoading) && !isStreamingActive && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: theme.palette.grey[100],
                  borderRadius: 3,
                  maxWidth: '70%',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  AI is typing...
                </Typography>
              </Paper>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* Input Section - Fixed at bottom */}
        <Box
          sx={{
            p: 2,
            backgroundColor: 'white',
            borderTop: `1px solid ${theme.palette.divider}`,
            flexShrink: 0, // Prevent shrinking
          }}
        >
          <Box
            component="form"
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 2,
              maxWidth: '100%',
            }}
            onSubmit={e => {
              e.preventDefault();
              handleSendMessage();
            }}
          >
            {/* Only show chat type selector when communication menu items are enabled */}
            {showCommunicationMenuItems && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={chatType}
                  onChange={e => setChatType(e.target.value as ChatType)}
                  displayEmpty
                  sx={{
                    height: '44px',
                    borderRadius: 3,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.divider,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <MenuItem value="documents">Documents Chat</MenuItem>
                  <MenuItem value="general">General Chat</MenuItem>
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              multiline
              maxRows={3}
              placeholder="Ask a question about your lease, bills, etc."
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSendMessage();
                }
              }}
              variant="outlined"
              disabled={isMutationLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  backgroundColor: 'white',
                  minHeight: '44px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isMutationLoading ? (
                      <CircularProgress size={20} color="primary" />
                    ) : (
                      <IconButton
                        color="primary"
                        disabled={!message.trim()}
                        onClick={handleSendMessage}
                        size="small"
                        sx={{
                          bgcolor: theme.palette.primary.main,
                          color: 'white',
                          '&:hover': {
                            bgcolor: theme.palette.primary.dark,
                          },
                          '&:disabled': {
                            bgcolor: theme.palette.grey[300],
                            color: theme.palette.grey[500],
                          },
                        }}
                      >
                        <Send01Icon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
            You're chatting with documents from: {selectedPropertyLocation?.name}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const ChatWithAIPage: React.FC = () => (
  <ChatWidgetProvider>
    <ChatInterface />
  </ChatWidgetProvider>
);

export default ChatWithAIPage;
